import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {ToggleSwitch} from '@Components/toggle-switch-v2';
import {ClickableDiv} from '@Components/clickable-div';
import {
  disableNextButton,
  enableNextButton,
  removeAccountFromPublishingParams,
  removeAccountIdFromPublishing,
  removeWizardSocialAccount,
  updatePublishingParamDisableComment,
  updatePublishingParamDisableDuet,
  updatePublishingParamDisableStitch,
  updatePublishingParamIsPromotingThirdParty,
  updatePublishingParamIsSelfBrand,
  updatePublishingParams,
  updateWizardSocialAccount,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {TiktokPrivacySelector} from '@Components/social-media-wizard/components/tiktok-privacy-selector';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {useAccountIdsToPublishTo, usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import type {TikTokPublishingParams} from '@Components/social-media/publish-params.types';
import {useWizardDesignsGraphicsMetaMap} from '@Components/social-media-wizard/social-media-wizard.hooks';
import styles from './tiktok-publishing-options.module.scss';
import {useAppDispatch} from '@/hooks';
import type {TikTokPrivacySettings} from '@/components/social-media/post.vo';
import {TikTokPrivateAccountPrivacySettings, TikTokPublicAccountPrivacySettings} from '@/components/social-media/post.vo';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {
  addConnectAccountBroadcastChannel,
  isSocialMediaAccountSharedByTeamMember,
  reAuthenticateInModal,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media-account.library';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';

interface TiktokPublishingOptionsProps {
  account: SocialAccount;
}

interface TikTokUpdateResponse {
  account: SocialAccount;
}

export function TiktokPublishingOptions({account}: TiktokPublishingOptionsProps): ReactElement<any> | null {
  const {tikTokMeta} = account;
  const [disableComment, setDisableComment] = useState(tikTokMeta ? tikTokMeta.isCommentDisabled : false);
  const [disableStitch, setDisableStitch] = useState(tikTokMeta ? tikTokMeta.isStitchDisabled : false);
  const [disableDuet, setDisableDuet] = useState(tikTokMeta ? tikTokMeta.isDuetDisabled : false);
  const [contentDisclosureToggle, setContentDisclosureToggle] = useState(false);
  const [isSelfBrand, setIsSelfBrand] = useState(false);
  const [isPromotingThirdParty, setIsPromotingThirdParty] = useState(false);
  const [isBrandSwitchDisabled, setIsBrandSwitchDisabled] = useState(false);
  const [isTikTokAPIQueried, setIsTikTokAPIQueried] = useState(false);
  const [isAccountValid, setIsAccountValid] = useState(true);
  const dispatch = useAppDispatch();
  const publishingParams = usePostPublishingParamsForAccount(account.id) as TikTokPublishingParams;
  const designsMetaMap = useWizardDesignsGraphicsMetaMap();
  const singleAccountToPublishTo = useAccountIdsToPublishTo().length === 1;

  const onDisableComment = (): void => {
    const newDisableComment = !disableComment;

    setDisableComment(newDisableComment);
    dispatch(
      updatePublishingParamDisableComment({
        idSocialMediaAccount: account.id,
        disableComment: newDisableComment,
      })
    );
  };

  const onDisableStitch = (): void => {
    const newDisableStitch = !disableStitch;

    setDisableStitch(newDisableStitch);
    dispatch(
      updatePublishingParamDisableStitch({
        idSocialMediaAccount: account.id,
        disableStitch: newDisableStitch,
      })
    );
  };

  const onDisableDuet = (): void => {
    const newDisableDuet = !disableDuet;

    setDisableDuet(!disableDuet);
    dispatch(
      updatePublishingParamDisableDuet({
        idSocialMediaAccount: account.id,
        disableDuet: newDisableDuet,
      })
    );
  };

  const doRefreshTikTokOptions = async (): Promise<void> => {
    try {
      const data = (await window.PMW.writeLocal(`socialmedia/updateTikTokAccountMeta/${account.id}`)) as TikTokUpdateResponse;

      dispatch(updateWizardSocialAccount(data.account));
      setIsTikTokAPIQueried(true);
      setIsAccountValid(true);
      dispatch(enableNextButton());
    } catch (e) {
      setIsAccountValid(false);

      if (singleAccountToPublishTo) {
        dispatch(disableNextButton());
      }
    }
  };

  const onIsSelfBrand = (): void => {
    const newIsSelfBrand = !isSelfBrand;

    setIsSelfBrand(newIsSelfBrand);
    dispatch(
      updatePublishingParamIsSelfBrand({
        idSocialMediaAccount: account.id,
        isSelfBrand: newIsSelfBrand,
      })
    );
  };

  const onIsPromotingThirdParty = (): void => {
    const newIsPromotingThirdParty = !isPromotingThirdParty;

    setIsPromotingThirdParty(!isPromotingThirdParty);
    dispatch(
      updatePublishingParamIsPromotingThirdParty({
        idSocialMediaAccount: account.id,
        isPromotingThirdParty: newIsPromotingThirdParty,
      })
    );
  };

  const onContentDisclosureToggle = (): void => {
    setContentDisclosureToggle(!contentDisclosureToggle);
  };

  const isThereAnyVideoPoster = (): boolean => {
    return Object.values(designsMetaMap).some((pageGraphicsMetaMap) => {
      return Object.values(pageGraphicsMetaMap).some((graphicMeta) => {
        return graphicMeta.isVideo;
      });
    });
  };

  const getPostMetaOptions = (): ReactElement<any> => {
    return (
      <div className="flex-h-row flex-content-between flex-wrap spacing-p-t-2 spacing-p-b-2">
        <div className="flex-center">
          <Icon icon="icon-group" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text val={window.i18next.t('pmwjs_allow_users_to')} size={TextSize.XSMALL} />
        </div>
        <div className="flex-h-row">
          {getAllowCommentElement()}
          {isThereAnyVideoPoster() ? getAllowDuetElement() : null}
          {isThereAnyVideoPoster() ? getAllowStitchElement() : null}
        </div>
      </div>
    );
  };

  const getAllowDuetElement = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={65} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <ClickableDiv className="flex-h-row spacing-m-r-4 _cursor-pointer" onClick={onDisableDuet}>
        <Checkbox isChecked={disableDuet} type={CHECKBOX_TYPE.PRIMARY} className="spacing-m-r-3" />
        <Text val={window.i18next.t('pmwjs_duet')} size={TextSize.XSMALL} className="content-body" />
      </ClickableDiv>
    );
  };

  const getAllowStitchElement = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={65} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <ClickableDiv className="flex-h-row _cursor-pointer" onClick={onDisableStitch}>
        <Checkbox isChecked={disableStitch} type={CHECKBOX_TYPE.PRIMARY} className="spacing-m-r-3" />
        <Text val={window.i18next.t('pmwjs_stitch')} size={TextSize.XSMALL} className="content-body" />
      </ClickableDiv>
    );
  };

  const getAllowCommentElement = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={65} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <ClickableDiv className="flex-h-row spacing-m-r-4 _cursor-pointer" onClick={onDisableComment}>
        <Checkbox isChecked={disableComment} type={CHECKBOX_TYPE.PRIMARY} className="spacing-m-r-3" />
        <Text val={window.i18next.t('pmwjs_comment')} size={TextSize.XSMALL} className="content-body" />
      </ClickableDiv>
    );
  };

  const getContentDisclosureToggle = (): ReactElement<any> => {
    return (
      <div className="flex-h-row flex-content-between">
        <div className="flex-center">
          <Icon icon="icon-video" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text val={window.i18next.t('pmwjs_content_disclosure_and_ads')} size={TextSize.XSMALL} />
        </div>
        <div>{getToggleSwitch()}</div>
      </div>
    );
  };

  const getToggleSwitch = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={60} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return <ToggleSwitch isDisabled={isBrandSwitchDisabled} isChecked={contentDisclosureToggle} onChange={onContentDisclosureToggle} />;
  };

  const getTextForTikTokAgreement = (): string => {
    if (isSelfBrand && !isPromotingThirdParty) {
      return window.i18next.t('pmwjs_agreement_music_tiktok');
    }

    return window.i18next.t('pmwjs_agreement_brand_and_music_tiktok');
  };

  const onTikTokPrivacySettingsSelected = (selectedOption: TikTokPrivacySettings): void => {
    if (selectedOption === TikTokPublicAccountPrivacySettings.SELF_ONLY || selectedOption === TikTokPrivateAccountPrivacySettings.SELF_ONLY) {
      setIsBrandSwitchDisabled(true);
      setIsPromotingThirdParty(false);
      setContentDisclosureToggle(false);
      setIsSelfBrand(false);
    } else {
      setIsBrandSwitchDisabled(false);
    }
  };

  const getContentDisclosureOptions = (): ReactElement<any> => {
    return (
      <div className={`flex-v-row ${styles.greyBox} ${contentDisclosureToggle ? styles.visible : ''}`}>
        <div className="flex-h-row spacing-m-b-4">
          <div className="spacing-m-l-2 spacing-m-r-3 spacing-m-t-1">
            <Checkbox type={CHECKBOX_TYPE.PRIMARY} isChecked={isSelfBrand} onClick={onIsSelfBrand} />
          </div>
          <div className="flex-v-row">
            <Text val={window.i18next.t('pmwjs_your_brand')} size={TextSize.XSMALL} bold className="spacing-m-b-1 content-body" />
            <Text val={window.i18next.t('pmwjs_video_labeled_promotional')} size={TextSize.XSMALL} className="content-sub-text" />
          </div>
        </div>
        <div className="flex-h-row spacing-m-b-4">
          <div className="spacing-m-l-2 spacing-m-r-3 spacing-m-t-1">
            <Checkbox type={CHECKBOX_TYPE.PRIMARY} isChecked={isPromotingThirdParty} onClick={onIsPromotingThirdParty} />
          </div>
          <div className="flex-v-row">
            <Text val={window.i18next.t('pmwjs_branded_content')} size={TextSize.XSMALL} bold className="spacing-m-b-1 content-body" />
            <Text val={window.i18next.t('pmwjs_your_video_paid_partnership')} size={TextSize.XSMALL} className="content-sub-text" />
          </div>
        </div>
        <div className="flex-h-row">
          <Icon icon="icon-info" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text className={styles.link} dangerouslySetInnerHTML val={getTextForTikTokAgreement()} size={TextSize.XSMALL} />
        </div>
      </div>
    );
  };

  const getTikTokPublishingOptions = (): ReactElement => {
    return (
      <>
        <TiktokPrivacySelector account={account} onSelectOption={onTikTokPrivacySettingsSelected} isLoading={!isTikTokAPIQueried} />
        <FieldSpacer />
        {getPostMetaOptions()}
        <FieldSpacer />
        {getContentDisclosureToggle()}
        <FieldSpacer />
        {getContentDisclosureOptions()}
      </>
    );
  };

  const getTikTokInvalidAccountMessage = (): ReactElement => {
    return (
      <div>
        <div className={styles.invalidAccountMessage}>
          <Icon icon="icon-exclamation-triangle" type={IconType.NONE} size={IconSize.SIZE_ICON_16} className="content-danger" />
          <Text val={window.i18next.t('pmwjs_social_media_tiktok_reconnect')} size={TextSize.XSMALL} className="content-body" />
        </div>
        <FieldSpacer />
        <div className={styles.accountCtaContainer}>
          <Button
            type={Type.PRIMARY}
            size={Size.XSMALL}
            iconPlacement={IconPlacement.LEFT}
            icon="icon-rotate-ccw"
            text={window.i18next.t('pmwjs_refresh')}
            title={window.i18next.t('pmwjs_refresh')}
            onClick={() => {
              reAuthenticateInModal(account.id, SocialPlatform.TIKTOK);
            }}
          />
          {getAccountDeleteButton()}
        </div>
      </div>
    );
  };

  const getAccountDeleteButton = (): ReactElement | null => {
    if (isSocialMediaAccountSharedByTeamMember(account)) {
      return null;
    }

    return (
      <Button
        type={Type.SECONDARY}
        size={Size.XSMALL}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-delete"
        iconClassName="content-danger"
        text={window.i18next.t('pmwjs_remove')}
        title={window.i18next.t('pmwjs_remove')}
        onClick={removeAccount}
      />
    );
  };

  const doRemoveAccountFromPost = (): void => {
    void window.PMW.writeLocal('socialmedia/deleteAccountForUser', {
      id: account.id,
    }).then((): void => {
      if (singleAccountToPublishTo) {
        dispatch(updatePublishingParams({}));
        dispatch(updateWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS));
      } else {
        dispatch(removeAccountIdFromPublishing(account.id));
        dispatch(removeAccountFromPublishingParams(account.id));
      }

      dispatch(removeWizardSocialAccount(account.id));
      trackPublishingGA4Events(GA4EventName.ACCOUNT_DELETED, {[GA4EventParamName.PLATFORM]: getSocialPlatformName(SocialPlatform.TIKTOK)});
    });
  };

  const removeAccount = (): void => {
    openConfirmDeleteModal({
      title: window.i18next.t('pmwjs_are_you_sure'),
      text: window.i18next.t('pmwjs_social_account_deletion_message'),
      warningIcon: 'icon-delete',
      ctaText: window.i18next.t('pmwjs_delete_account'),
      onDeleteConfirmation: doRemoveAccountFromPost,
    });
  };

  const onAccountReconnect = (): void => {
    void doRefreshTikTokOptions();
  };

  useEffect(() => {
    let accountCreatedBroadcastChannel: BroadcastChannel | undefined = undefined;

    if (publishingParams) {
      setIsSelfBrand(publishingParams.isSelfBrand);
      setIsPromotingThirdParty(publishingParams.isPromotingThirdParty);
      setDisableComment(publishingParams.disableComment);
      setDisableStitch(publishingParams.disableStitch);
      setDisableDuet(publishingParams.disableDuet);

      if (publishingParams.isSelfBrand || publishingParams.isPromotingThirdParty) {
        setContentDisclosureToggle(true);
      }

      accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(onAccountReconnect);
    }

    void doRefreshTikTokOptions();

    return (): void => {
      if (accountCreatedBroadcastChannel) {
        removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
      }
    };
  }, []);

  return isAccountValid ? getTikTokPublishingOptions() : getTikTokInvalidAccountMessage();
}
